import React, { useEffect } from 'react';
import { isBlank } from 'utils/helperMethods';
import { head } from 'ramda';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { Box, Typography, Button } from '@material-ui/core';
import { ToggleButtonGroup, ToggleButton } from '@material-ui/lab';
import MortgageSurveyQuestionPresenter from 'presenters/MortgageSurveyQuestionPresenter';
import MortgageSurveyPresenter from 'presenters/MortgageSurveyPresenter';
import InputWithSlider from 'sharedComponents/InputWithSlider';
import { formatMoneyWithSign } from 'utils/currencyHelper';

import useStyles from './useStyles';

const HIGH_SALARY_DTI_RATIO = 3.00274994523;
// const MID_SALARY_DTI_RATIO = 3.60329993427;
const LOW_SALARY_DTI_RATIO = 4.30394158816;

const PriceTargetQuestion = props => {
  const { question, onChange, surveyValues, onValidate, onNext, isValid, onSelect } = props;
  const { t } = useTranslation();
  const classes = useStyles();

  const field = head(MortgageSurveyQuestionPresenter.surveyValueFields(question));
  const currentValue = MortgageSurveyPresenter[field](surveyValues);
  const flowCurrentValue = MortgageSurveyPresenter.purpose(surveyValues);

  useEffect(() => {
    const isValueValid = !isBlank(currentValue) && currentValue > 0;
    onValidate(isValueValid);
  }, [currentValue]);

  const options = MortgageSurveyQuestionPresenter.options(question);
  const { min, max, marksDivider } = options;

  const dtiSalaryHigh = formatMoneyWithSign((currentValue / HIGH_SALARY_DTI_RATIO).toFixed(0), 'USD');
  // const dtiSalaryMid = formatMoneyWithSign((currentValue / MID_SALARY_DTI_RATIO).toFixed(0), 'USD');
  const dtiSalaryLow = formatMoneyWithSign((currentValue / LOW_SALARY_DTI_RATIO).toFixed(0), 'USD');

  const handleValueChange = value => {
    onChange([{ field, value }]);
  };

  const handleToggle = (event: React.MouseEvent<HTMLElement>, newValue: string) => {
    if (newValue) {
      onSelect(newValue);
    }
  };

  return (
    <Box width="100%">
      <div className={classes.buttonBox}>
        <ToggleButtonGroup value={flowCurrentValue} exclusive onChange={handleToggle} aria-label="Platform">
          <ToggleButton value="purchase">Buying a Home</ToggleButton>
          <ToggleButton value="refinance">Refinancing</ToggleButton>
        </ToggleButtonGroup>
      </div>
      <Box mt={4} />
      <Typography variant="h1">{MortgageSurveyQuestionPresenter.label(question)}</Typography>
      <Box mt={1} />
      <Typography variant="body1">{MortgageSurveyQuestionPresenter.description(question)}</Typography>
      <Box mt={1} />
      <InputWithSlider
        className={classes.input}
        value={currentValue}
        min={min}
        max={max}
        marksDivider={marksDivider}
        onChange={handleValueChange}
      />
      <Box width="100%" className={classes.buttonBox}>
        <Button onClick={onNext} className={classes.button} variant="contained" disabled={!isValid}>
          {t('components.MortgageSurvey.ControlBar.nextButton')}
        </Button>
      </Box>
      <Box width="100%" className={classes.additionalInfo}>
        <Box mt={3} mb={1} display="flex" width="100%" flexWrap="wrap" justifyContent="center">
          <Box display="flex" flexDirection="column" alignItems="center" my={1}>
            <Typography variant="h1">{dtiSalaryLow}</Typography>
            <Typography variant="subtitle1" color="inherit" className={classes.errorText} align="center">
              {t('components.MortgageSurvey.PriceTargetQuestion.agressiveIncome')}
            </Typography>
          </Box>

          <Box display="flex" flexDirection="column" my={1}>
            <Typography variant="h1"> to </Typography>
          </Box>
          <Box display="flex" flexDirection="column" alignItems="center" my={1}>
            <Typography variant="h1">{dtiSalaryHigh}</Typography>
            <Typography variant="subtitle1" color="inherit" className={classes.validText} align="center">
              {t('components.MortgageSurvey.PriceTargetQuestion.suggestedIncome')}
            </Typography>
          </Box>
        </Box>

        <Box display="flex" width="100%" flexWrap="wrap" justifyContent="center">
          <Typography variant="body1">
            {t('components.MortgageSurvey.PriceTargetQuestion.estimatedAnnualIncome')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

PriceTargetQuestion.propTypes = {
  question: MortgageSurveyQuestionPresenter.shape().isRequired,
  onChange: PropTypes.func.isRequired,
  surveyValues: MortgageSurveyPresenter.shape().isRequired,
  onValidate: PropTypes.func.isRequired,
};

export default PriceTargetQuestion;



// WEBPACK FOOTER //
// ./src/sharedComponents/MortgageSurvey/components/PriceTargetQuestion/PriceTargetQuestion.js